// YouTube mode shell (clone of ../components/Layout.tsx) — same 52px header,
// wordmark "youtube", and on the right: [rate] [fill queue] [stats] [export]
// [← cinematic] theme toggle, settings, logout. Content renders through the
// router <Outlet/>. Marks <html data-mode="youtube"> while mounted.
//
// Access: the shell itself is reachable by URL for any signed-in user, but
// every /api/youtube/* call except /access returns 403 for non-allowlisted
// users — the shell shows a plain notice instead of the navigator then.

import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";

import { apiFetch, ApiError } from "../api/client";
import { Logo } from "../components/Logo";
import { getStoredTheme, setStoredTheme, type Theme } from "../theme";
import { fillQueue, useYtAccess, ytKeys } from "./api";

import "../components/Layout.css";
import "./youtube.css";

export function YoutubeLayout() {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme());
  const navigate = useNavigate();
  const { data: access } = useYtAccess();

  useEffect(() => {
    document.documentElement.setAttribute("data-mode", "youtube");
    return () => document.documentElement.removeAttribute("data-mode");
  }, []);

  function toggleTheme() {
    const next: Theme = theme === "light" ? "dark" : "light";
    setTheme(next);
    setStoredTheme(next);
  }

  async function logout() {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // Even if the request fails, drop to the login screen.
    }
    navigate("/login", { replace: true });
  }

  const eligible = access?.eligible ?? null;

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link to="/youtube" className="app-header__logo" aria-label="youtube mode — home">
          <span className="app-header__glyph">
            <Logo size={20} />
          </span>
          <span className="app-header__wordmark">youtube</span>
        </Link>

        <div className="app-header__spacer" />

        <button
          type="button"
          className="app-header__btn app-header__btn--primary"
          title="step through the queue — rate each video's relevance"
          onClick={() => navigate("/youtube?list=queue&rate=1")}
          disabled={!eligible}
        >
          rate
        </button>

        <FillControl disabled={!eligible} />

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          title="queue statistics — how each fully rated queue scored"
          onClick={() => navigate("/youtube/stats")}
          disabled={!eligible}
        >
          stats
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          title="download your rated youtube history as a CSV file"
          onClick={() => {
            window.location.href = ytKeys.exportCsv;
          }}
          disabled={!eligible}
        >
          export
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          title="back to the cinematic database"
          onClick={() => navigate("/")}
        >
          ← cinematic
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--ghost"
          onClick={toggleTheme}
          aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
        >
          {theme === "light" ? "☾" : "☀"}
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--ghost app-header__btn--settings"
          title="settings"
          aria-label="Settings"
          onClick={() => navigate("/settings")}
        >
          ⚙
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          onClick={() => void logout()}
        >
          logout
        </button>
      </header>

      <main className="app-main">
        {eligible === false ? (
          <div className="details details--empty">youtube mode is not available for this user</div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}

// ── Fill button + result toasts ────────────────────────────────────────────

interface ToastState {
  kind: "ok" | "error";
  text: string;
}

function FillControl({ disabled }: { disabled: boolean }) {
  const { mutate } = useSWRConfig();
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimer = useRef<number | null>(null);

  function showToast(next: ToastState, ms = 5000) {
    if (toastTimer.current != null) window.clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  }

  useEffect(() => {
    return () => {
      if (toastTimer.current != null) window.clearTimeout(toastTimer.current);
    };
  }, []);

  async function fill() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fillQueue();
      showToast({
        kind: "ok",
        text:
          res.added === 0
            ? "nothing left to add — every video is on a list"
            : `added ${res.added} videos · queue ${res.queue_total}`,
      });
      void mutate(ytKeys.lists);
      void mutate(ytKeys.list("queue"));
      void mutate(ytKeys.stats);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        showToast({ kind: "ok", text: "queue is full — rate some videos first" }, 3500);
      } else if (e instanceof ApiError && e.status === 503) {
        showToast({ kind: "error", text: "youtube history database not found on the server" });
      } else {
        showToast({ kind: "error", text: "could not fill the queue" });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="app-header__btn app-header__btn--primary"
        title="top the queue up to 100 random videos from your watch history"
        onClick={() => void fill()}
        disabled={disabled || busy}
      >
        {busy ? "filling…" : "fill queue"}
      </button>

      {toast && (
        <div
          className={`toast toast--${toast.kind}`}
          role={toast.kind === "error" ? "alert" : "status"}
        >
          {toast.text}
        </div>
      )}
    </>
  );
}
